import { useState } from 'react'
import { followupsApi } from '../api'
import { FollowupType } from '../types'

interface FollowupFormProps {
  leadId: number
  onClose: () => void
  onSaved: () => void
}

export default function FollowupForm({ leadId, onClose, onSaved }: FollowupFormProps) {
  const [formData, setFormData] = useState({
    scheduled_date: '',
    type: 'call' as FollowupType,
    notes: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await followupsApi.create({
        lead_id: leadId,
        scheduled_date: formData.scheduled_date,
        type: formData.type,
        notes: formData.notes,
      })
      onSaved()
    } catch (err) {
      setError('Failed to save follow-up. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end lg:items-center justify-center z-50">
      <div className="bg-white rounded-t-xl lg:rounded-xl shadow-xl w-full lg:max-w-md">
        <div className="p-4 lg:p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <h2 className="text-lg lg:text-xl font-semibold">Schedule Follow-up</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-3 lg:space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="label text-xs lg:text-sm">Date & Time *</label>
            <input
              type="datetime-local"
              required
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
              className="input-field text-sm lg:text-base"
            />
          </div>

          <div>
            <label className="label text-xs lg:text-sm">Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as FollowupType })}
              className="input-field text-sm lg:text-base"
            >
              <option value="call">📞 Call</option>
              <option value="whatsapp">💬 WhatsApp</option>
              <option value="email">📧 Email</option>
              <option value="meeting">🤝 Meeting</option>
              <option value="demo">🎯 Demo</option>
            </select>
          </div>

          <div>
            <label className="label text-xs lg:text-sm">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input-field text-sm lg:text-base"
              rows={3}
              placeholder="Add any notes for this follow-up..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary text-sm lg:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary text-sm lg:text-base disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Schedule Follow-up'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
